import * as React from 'react';

import { getApplicationDomain } from '@gitbook/env';
import { LoadingPanel } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { PublishedContentFallback, useHideSplashscreen } from '../../Components';
import { useHistory } from '../../navigation';
import {
    type FirebaseActionURLParams,
    useCurrentUser,
    useFirebaseActionURLParams,
    useIsRedirectedForAuth,
} from '../../state';
import { LoginScreen } from './LoginScreen';
import { StepApplyAuthRedirect } from './StepApplyAuthRedirect';
import { StepApplyPasswordResetLink } from './StepApplyPasswordResetLink';
import { StepApplyRecoverEmailLink } from './StepApplyRecoverEmailLink';
import { StepApplySignInLink } from './StepApplySignInLink';
import { StepApplyVerifyAndChangeEmailLink } from './StepApplyVerifyAndChangeEmailLink';
import { StepApplyVerifyEmailLink } from './StepApplyVerifyEmailLink';
import { StepRedirectToContinueUrl } from './StepRedirectToContinueUrl';
import { StepRedirectToGitHubAuth } from './StepRedirectToGitHubAuth';

/**
 * Show login screen if the user is unauthenticated.
 */
export function LoginScreenWrapper(props: { children: React.ReactNode }): React.ReactElement {
    const firebaseUser = useCurrentUser();
    const firebaseAction = useFirebaseActionURLParams();

    const isRedirecting = useIsRedirectingToContinueUrl(firebaseAction?.continueUrl);
    const shouldRedirectToAuth = useIsStartingRedirectFlow();
    const isAuthenticatingWithRedirect = useIsRedirectedForAuth();

    if (isAuthenticatingWithRedirect) {
        return <StepApplyAuthRedirect onDone={isAuthenticatingWithRedirect} />;
    }

    if (isRedirecting) {
        return <StepRedirectToContinueUrl />;
    }

    if (shouldRedirectToAuth && !firebaseUser) {
        return <StepRedirectToGitHubAuth />;
    }

    // When the user is logging-in, we let the login screen handles the login logic
    // ex: it allows to login on desktop (redirect done by LoginScreen) when the user is already logged-in
    if (firebaseAction) {
        return <FirebaseActionsWrapper firebaseAction={firebaseAction} />;
    }

    if (!firebaseUser) {
        // Before falling through to the login screen, see if the URL points to content that has
        // a public version: surface the [[PublishedContentCTAPage]] so the visitor can read the
        // published site without signing in. Anything else (no space/site in the URL, 404 from
        // the resolve endpoint, or user explicitly chose "Login to edit") falls back to login.
        return <PublishedContentFallback fallback={<LoginScreen />} />;
    }

    return <React.Suspense fallback={<LoadingPanel />}>{props.children}</React.Suspense>;
}

/**
 * Handle the Firebase action URL parameters.
 */
function FirebaseActionsWrapper(props: {
    firebaseAction: FirebaseActionURLParams;
}): React.ReactElement {
    const { firebaseAction } = props;
    useHideSplashscreen(0);

    switch (firebaseAction.mode) {
        case 'resetPassword':
            return <StepApplyPasswordResetLink action={firebaseAction} />;
        case 'signIn':
            return <StepApplySignInLink />;
        case 'verifyEmail':
            return <StepApplyVerifyEmailLink action={firebaseAction} />;
        case 'recoverEmail':
            return <StepApplyRecoverEmailLink action={firebaseAction} />;
        case 'verifyAndChangeEmail':
            return <StepApplyVerifyAndChangeEmailLink action={firebaseAction} />;
        default:
            assertNever(firebaseAction.mode);
    }
}

/**
 * The continueUrl is the original url where the user action took place.
 * Our authentication can happen on any url if it's on the correct domain.
 */
function useIsRedirectingToContinueUrl(continueUrl: string | undefined): boolean {
    const history = useHistory();
    const currentUrl = window.location.href;

    const redirect = React.useMemo(() => {
        const parsedCurrentUrl = new URL(currentUrl);
        const parsedContinueUrl = continueUrl ? new URL(continueUrl) : null;

        const redirectTo =
            parsedContinueUrl &&
            // For security reasons, we only allow redirecting to a continue-url we own.
            // For development, 127.0.0.1 is accepted.
            parsedContinueUrl.host === getApplicationDomain()
                ? parsedContinueUrl
                : parsedCurrentUrl;

        const isSameHost = parsedCurrentUrl.host === redirectTo.host;
        const isSamePath = parsedCurrentUrl.pathname === redirectTo.pathname;

        const searchParams = new URLSearchParams(redirectTo.searchParams);
        for (const [key, value] of parsedCurrentUrl.searchParams.entries()) {
            if (!searchParams.has(key)) {
                searchParams.append(key, value);
            }
        }
        const isSameSearch = parsedCurrentUrl.searchParams.toString() === searchParams.toString();
        const isSameHash = parsedCurrentUrl.hash === redirectTo.hash;

        if (!isSameHost) {
            // Full redirect because we are changing host
            redirectTo.search = searchParams.toString();
            return { url: redirectTo.toString() };
        }
        if (!isSamePath || !isSameSearch || !isSameHash) {
            redirectTo.search = searchParams.toString();
            // Replace path when redirecting on same host
            return {
                path: redirectTo.pathname,
                search: redirectTo.search,
                hash: redirectTo.hash,
            };
        }

        return null;
    }, [continueUrl, currentUrl]);

    React.useEffect(() => {
        if (redirect?.url) {
            window.location.href = redirect.url;
        } else if (redirect?.path) {
            history.replace({
                pathname: redirect.path,
                search: redirect.search,
                hash: redirect.hash,
            });
        }
    }, [history, redirect]);

    return Boolean(redirect);
}

/**
 * Return true if we should start a redirect flow.
 * It's done by passing ?auth=github into the url.
 */
function useIsStartingRedirectFlow(): boolean {
    return React.useMemo(() => {
        const url = new URL(window.location.href);
        return url.searchParams.get('auth') === 'github';
    }, []);
}
